import { Loader2 } from 'lucide-react';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useLeaveTypeForm } from '../hooks/useLeaveTypeForm';
import type { LeaveType } from '../types/leaveType.types';

interface LeaveTypeFormModalProps {
  leaveType: LeaveType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeaveTypeFormModal({ leaveType, open, onOpenChange }: LeaveTypeFormModalProps) {
  const {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  } = useLeaveTypeForm({
    leaveType,
    open,
    onClose: () => onOpenChange(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  }

  const title = isEditMode ? 'Edit Leave Type' : 'Create Leave Type';
  const description = isEditMode
    ? `Update details for "${leaveType?.name}".`
    : 'Add a new leave type employees can apply for.';
  const submitLabel = isEditMode ? 'Update' : 'Create';
  const submittingLabel = isEditMode ? 'Updating...' : 'Creating...';

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        placeholder="CASUAL"
                        autoComplete="off"
                        className="uppercase"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Casual Leave" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        placeholder="Optional description shown to employees"
                        rows={3}
                        className="min-h-20 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring/30 resize-y"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={field.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange(true)}
                        >
                          Active
                        </Button>
                        <Button
                          type="button"
                          variant={!field.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange(false)}
                        >
                          Inactive
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={requestClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      {submittingLabel}
                    </>
                  ) : (
                    submitLabel
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && cancelDiscard()}
        onDiscard={confirmDiscard}
      />
    </>
  );
}
